import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getEvent } from '../api/client';
import { useNow, usePolling } from '../api/hooks';
import { DeliveryTimeline } from '../components/AttemptTimeline';
import { CopyButton } from '../components/CopyButton';
import { JsonBlock } from '../components/JsonBlock';
import { EmptyState, ErrorBanner, ErrorState, LoadingState } from '../components/StateViews';
import { DeliveryStatusBadge } from '../components/StatusBadge';
import { hasPendingDeliveries, rollUpDeliveries } from '../lib/deliveries';
import { formatAbsolute, formatDuration, formatRelativeTime } from '../lib/format';

const POLL_INTERVAL_MS = 2000;

export function EventDetailView() {
  const parameters = useParams<{ id: string }>();
  const id = parameters.id ?? '';

  // Retries land over seconds, so keep polling until every delivery is terminal.
  const [livePolling, setLivePolling] = useState(true);

  const fetcher = useCallback((signal: AbortSignal) => getEvent(id, signal), [id]);
  const { data, error, refresh } = usePolling(fetcher, POLL_INTERVAL_MS, livePolling);
  const now = useNow(1000);

  useEffect(() => {
    setLivePolling(true);
  }, [id]);

  useEffect(() => {
    if (data === null) return;
    setLivePolling(hasPendingDeliveries(data.deliveries));
  }, [data]);

  if (error !== null && data === null) {
    return (
      <div className="view">
        <Link className="back-link" to="/">
          ← Events
        </Link>
        <ErrorState error={error} onRetry={refresh} context="this event" />
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="view">
        <Link className="back-link" to="/">
          ← Events
        </Link>
        <LoadingState label="Loading event" rows={5} />
      </div>
    );
  }

  const { event, deliveries } = data;
  const rollup = rollUpDeliveries(deliveries);

  const latencies = deliveries
    .map((delivery) => delivery.latencyMs)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const slowest = latencies.length > 0 ? Math.max(...latencies) : null;
  const fullyDelivered = rollup.total > 0 && rollup.succeeded === rollup.total;

  return (
    <div className="view">
      <Link className="back-link" to="/">
          ← Events
      </Link>

      {error !== null ? <ErrorBanner error={error} onRetry={refresh} /> : null}

      <header className="view__head detail__head">
        <div>
          <p className="view__kicker">Event</p>
          <div className="detail__eyebrow">
            <DeliveryStatusBadge status={rollup.overall === 'empty' ? 'pending' : rollup.overall} />
            {livePolling ? (
              <span className="live live--on">
                <span className="live__dot" aria-hidden="true" />
                Live
              </span>
            ) : (
              <span className="muted">Settled</span>
            )}
          </div>
          <h1 className="view__title">{event.eventType}</h1>
          <div className="detail__id">
            <code className="mono">{event.id}</code>
            <CopyButton value={event.id} />
          </div>
        </div>

        <dl className="detail__facts">
          <div className="fact">
            <dt>Ingested</dt>
            <dd title={formatAbsolute(event.createdAt)}>{formatRelativeTime(event.createdAt, now)}</dd>
          </div>
          <div className="fact">
            <dt>Deliveries</dt>
            <dd>{rollup.label}</dd>
          </div>
          <div className="fact">
            <dt>{fullyDelivered ? 'Fully delivered in' : 'Slowest success'}</dt>
            <dd className="mono">{formatDuration(slowest)}</dd>
          </div>
          {event.benchmarkRunId ? (
            <div className="fact">
              <dt>Benchmark run</dt>
              <dd className="mono">{event.benchmarkRunId}</dd>
            </div>
          ) : null}
          {event.idempotencyKey ? (
            <div className="fact">
              <dt>Idempotency key</dt>
              <dd className="mono">{event.idempotencyKey}</dd>
            </div>
          ) : null}
        </dl>
      </header>

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Delivery timeline</h2>
          <p className="panel__lede">
            One block per endpoint. Vertical distance between attempts is proportional to the wait
            that preceded each one, so exponential backoff shows up as widening gaps.
          </p>
        </div>

        {deliveries.length === 0 ? (
          <EmptyState title="No deliveries for this event">
            The event was ingested, but it fanned out to nothing — either no endpoint subscribes to{' '}
            <code className="mono">{event.eventType}</code>, or none were registered at the time.
          </EmptyState>
        ) : (
          <div className="delivery-list">
            {deliveries.map((delivery) => (
              <DeliveryTimeline
                key={delivery.id}
                delivery={delivery}
                eventCreatedAt={event.createdAt}
                now={now}
              />
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Payload</h2>
          <p className="panel__lede">Exactly what was ingested, and what each attempt POSTed.</p>
        </div>
        <JsonBlock value={event.payload} label={event.eventType} />
      </section>
    </div>
  );
}
